import { CloseIcon } from '@chakra-ui/icons'
import { Button, Flex, Text } from '@chakra-ui/react'
import { ScreenContext, useApp } from '@hb/react'
import {
  AssessmentDocument,
  capitalizeFirstLetterOfEachWord,
  ConsentForm,
  FaxDocumentArgs,
  GenericFaxArgs,
  isConsentForm,
  isPracticeContract,
  SignableDocument,
  templateTypeNames,
  WithId,
} from '@hb/shared'
import { httpsCallable } from 'firebase/functions'
import { useCallback, useContext } from 'react'
import { functions } from '../../../backend/functions/functions'
import { ElectronicConsentViewButton } from '../../Users/Profile/ConsentForms/ElectronicConsent/ElectronicConsentModal'
import { EditorFaxButton, useOnFax } from '../EditorFaxButton'

const faxDocumentFunction = httpsCallable<FaxDocumentArgs>(functions, 'faxDocument')
const DocumentViewFaxButton = ({
  document,
}: {
  document: WithId<AssessmentDocument> | WithId<ConsentForm>
}) => {
  const handleFax = useCallback(
    async (args: GenericFaxArgs) => {
      const { faxToNumber, fileIds, notes, assessmentId } = args
      const { patientId } = document
      const documentType = isConsentForm(document) ? 'consent-form' : 'assessment-document'
      try {
        await faxDocumentFunction({
          faxToNumber,
          fileIds,
          patientId,
          documentId: document.id,
          documentType,
          html: '',
          notes,
          assessmentId,
        })
        return { success: 'Fax sent' }
      } catch (error) {
        const errMessage = error instanceof Error ? error.message : 'Unknown error'
        return { error: `Failed to send fax: ${errMessage}` }
      }
    },
    [document],
  )
  const faxManager = useOnFax(handleFax)
  return <EditorFaxButton {...faxManager} />
}

export const DocumentViewHeader = ({
  onBack,
  adminView,
  document,
}: {
  onBack?: () => void
  adminView?: boolean
  document: WithId<AssessmentDocument> | WithId<SignableDocument>
}) => {
  const { appName } = useApp()
  const { isMobile } = useContext(ScreenContext)

  const closeButton = onBack ? (
    <Button
      variant="outline"
      onClick={onBack}
      size="xs"
      ml="auto"
      bg="gray.50"
      color="gray.500"
      alignItems="center"
      gap={1}>
      <CloseIcon position="relative" top="1px" w={2} />
      <Text>Close</Text>
    </Button>
  ) : null

  return (
    <Flex
      width="100%"
      px={2}
      py={1}
      flexFlow={isMobile ? 'column' : 'row'}
      align={isMobile ? 'flex-start' : 'center'}
      zIndex={4}
      bg="gray.100"
      borderBottom="1px solid"
      borderColor="gray.200">
      <Flex
        p={1}
        align="center"
        gap={2}
        flex={isMobile ? 'unset' : 1}
        w={isMobile ? '100%' : 'auto'}
        minW="0">
        <Text fontWeight={500} fontFamily="Hero-New" noOfLines={1}>
          {document.name ?? capitalizeFirstLetterOfEachWord(templateTypeNames[document.type])}
        </Text>
        {isMobile ? closeButton : null}
      </Flex>
      <Flex
        flex={isMobile ? 'unset' : 'unset'}
        w={isMobile ? '100%' : 'unset'}
        justify={isMobile ? 'space-between' : 'flex-end'}
        align="center"
        gap={2}>
        {adminView && appName === 'app' && !isPracticeContract(document) ? (
          <DocumentViewFaxButton document={document} />
        ) : null}
        <ElectronicConsentViewButton />
        {isMobile ? null : closeButton}
      </Flex>
    </Flex>
  )
}
